import React, { useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion, AnimatePresence } from 'framer-motion';
import PharmacyLogin from '../../../components/pharmacy/form/login.js';
import PharmacyRegister from '../../../components/pharmacy/form/registration.js';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { BG_IMAGE_URLS, TOASTIFY_NEWEST_ON_TOP } from './config/config.js';
import BackToHome from '../../../components/hospital/navigation/BackToHome.js';

const PharmacyAuth = ({ isLoginPage = false }) => {
	const [isLogin, setIsLogin] = useState(isLoginPage);
	const navigate = useNavigate();

	return (
		<div className={css(styles.container)}>
			<div className={css(styles.contentWrapper)}>
				{/* Image section with better responsive behavior */}
				<div className={css(styles.imageSection)}>
					<img
						src={BG_IMAGE_URLS.pharmacyauthPage}
						alt="Pharmacy illustration"
						className={css(styles.image)}
					/>
				</div>

				{/* Form section with better responsive behavior */}
				<motion.div
					className={css(styles.formContainer)}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					<div className={css(styles.formInnerWrapper)}>
						<h1 className={css(styles.title)}>
							{isLogin ? 'Welcome back' : 'Create a new account'}
						</h1>
						<BackToHome />
						<AnimatePresence mode="wait">
							<motion.div
								key={isLogin ? 'login' : 'register'}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: 20 }}
								transition={{ duration: 0.3 }}
								className={css(styles.formWrapper)}
							>
								{isLogin ? (
									<PharmacyRegister toast={toast} />
								) : (
									<PharmacyLogin toast={toast} />
								)}
							</motion.div>
						</AnimatePresence>
						<p className={css(styles.toggleText)}>
							{isLogin
								? "Don't have an account? "
								: 'Already have an account? '}
							<span
								className={css(styles.toggleLink)}
								onClick={() => {
									setIsLogin(!isLogin);
									navigate(
										isLogin ? '/app/pharmacy/login' : '/app/pharmacy/register',
									);
								}}
							>
								{isLogin ? 'Login here' : 'Register here'}
							</span>
						</p>
					</div>
				</motion.div>
			</div>
			<ToastContainer newestOnTop={TOASTIFY_NEWEST_ON_TOP} />
		</div>
	);
};

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '100vh',
		backgroundColor: '#f0f4f8',
		fontFamily: "'Roboto', sans-serif",
		padding: '1rem',
		width: '100%',
	},
	contentWrapper: {
		display: 'flex',
		maxWidth: '1200px',
		width: '100%',
		backgroundColor: 'white',
		borderRadius: '12px',
		overflow: 'hidden',
		boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
		flexDirection: 'row',
		'@media (max-width: 768px)': {
			flexDirection: 'column',
			maxWidth: '500px',
		},
	},
	imageSection: {
		flex: '1 1 50%',
		display: 'block',
		position: 'relative',
		maxHeight: '800px',
		'@media (max-width: 992px)': {
			flex: '1 1 40%',
		},
		'@media (max-width: 768px)': {
			flex: 'none',
			height: '200px',
		},
		'@media (max-width: 480px)': {
			height: '150px',
		},
	},
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		objectPosition: 'center',
	},
	formContainer: {
		flex: '1 1 50%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		padding: '2rem',
		'@media (max-width: 992px)': {
			flex: '1 1 60%',
			padding: '1.5rem',
		},
		'@media (max-width: 768px)': {
			flex: 'none',
			width: '100%',
		},
		'@media (max-width: 480px)': {
			padding: '1rem',
		},
	},
	formInnerWrapper: {
		width: '100%',
		maxWidth: '450px',
		margin: '0 auto',
	},
	formWrapper: {
		width: '100%',
	},
	title: {
		color: 'black',
		textAlign: 'center',
		marginBottom: '1.5rem',
		fontSize: '2rem',
		fontWeight: '700',
		'@media (max-width: 480px)': {
			fontSize: '1.5rem',
			marginBottom: '1rem',
		},
	},
	toggleText: {
		textAlign: 'center',
		marginTop: '1.5rem',
		color: '#4a5568',
		'@media (max-width: 480px)': {
			marginTop: '1rem',
			fontSize: '0.9rem',
		},
	},
	toggleLink: {
		color: '#2783F7',
		cursor: 'pointer',
		fontWeight: '600',
		':hover': {
			textDecoration: 'underline',
		},
	},
});

export default PharmacyAuth;
